import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { employeeService } from '../../../services/employeeService';

const EmployeeSelector = ({ value, onChange, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await employeeService?.getAll();
      const activeEmployees = (data || [])?.filter(emp => emp?.activo)?.map(emp => ({
        id: emp?.id,
        nombre: emp?.nombre,
        rol: emp?.rol || 'Empleado',
      }));
      setEmployees(activeEmployees);
    } catch (err) {
      setLoadError(err?.message || 'Error al cargar empleados');
    } finally {
      setLoading(false);
    }
  };

  const selectedEmployee = employees?.find(e => e?.id === value);
  const filteredEmployees = employees?.filter(emp =>
    emp?.nombre?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors focus:outline-none
          ${error ? 'border-[#E05252]/50' : 'border-white/10 hover:border-white/20 focus:border-[#D6EF4E]/40'}
          ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          bg-[#111111]`}
      >
        {loading ? (
          <span className="text-sm font-oranienbaum text-white/30">Cargando empleados...</span>
        ) : selectedEmployee ? (
          <div className="flex items-center gap-2.5 flex-1 text-left min-w-0">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-oranienbaum font-bold text-sm"
              style={{ background: 'rgba(214,239,78,0.1)', color: '#D6EF4E' }}>
              {selectedEmployee?.nombre?.charAt(0)}
            </div>
            <span className="text-sm font-oranienbaum text-white/85 truncate">{selectedEmployee?.nombre}</span>
          </div>
        ) : (
          <span className="text-sm font-oranienbaum text-white/30">Seleccionar empleado...</span>
        )}
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={14} style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0, marginLeft: 8 }} />
      </button>

      {error && <p className="text-[11px] text-[#E05252] mt-1 font-oranienbaum">{error}</p>}
      {loadError && <p className="text-[11px] text-[#E05252] mt-1 font-oranienbaum">{loadError}</p>}

      {/* Selected employee info */}
      {selectedEmployee && (
        <div className="mt-2 p-3 bg-[#111111] rounded-lg border border-white/8 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-oranienbaum font-bold text-sm"
            style={{ background: 'rgba(214,239,78,0.12)', color: '#D6EF4E' }}>
            {selectedEmployee?.nombre?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-oranienbaum font-semibold text-white/85 truncate">{selectedEmployee?.nombre}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-oranienbaum font-semibold"
                style={{ background: 'rgba(58,127,107,0.12)', color: '#3A7F6B', border: '1px solid rgba(58,127,107,0.2)' }}>
                <Icon name="CheckCircle" size={10} />
                Disponible
              </span>
              {selectedEmployee?.rol && (
                <span className="text-[10px] font-oranienbaum text-white/35">{selectedEmployee?.rol}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1.5 bg-[#1A1A1A] rounded-xl shadow-2xl border border-white/10 overflow-hidden">
          {/* Search */}
          <div className="p-2.5 border-b border-white/8">
            <div className="relative">
              <Icon name="Search" size={13} style={{ color: 'rgba(255,255,255,0.35)', position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full bg-[#111111] border border-white/10 text-white/85 text-xs rounded-lg pl-8 pr-3 py-2 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center">
                <div className="w-5 h-5 border-2 border-[#D6EF4E]/30 border-t-[#D6EF4E] rounded-full animate-spin mx-auto mb-2" />
                <p className="text-xs font-oranienbaum text-white/35">Cargando...</p>
              </div>
            ) : loadError ? (
              <div className="p-6 text-center">
                <Icon name="AlertCircle" size={24} style={{ color: '#E05252', margin: '0 auto 8px' }} />
                <p className="text-xs font-oranienbaum text-[#E05252]">{loadError}</p>
              </div>
            ) : filteredEmployees?.length > 0 ? (
              filteredEmployees?.map((emp) => (
                <button
                  key={emp?.id}
                  type="button"
                  onClick={() => {
                    onChange(emp?.id);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 transition-colors text-left
                    ${value === emp?.id ? 'bg-[#D6EF4E]/8' : 'hover:bg-white/5'}`}
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-oranienbaum font-bold text-sm"
                    style={{ background: 'rgba(214,239,78,0.08)', color: '#D6EF4E' }}>
                    {emp?.nombre?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-oranienbaum text-white/85 truncate">{emp?.nombre}</p>
                    {emp?.rol && (
                      <p className="text-[10px] font-oranienbaum text-white/35 truncate">{emp?.rol}</p>
                    )}
                  </div>
                  {value === emp?.id && (
                    <Icon name="Check" size={13} style={{ color: '#D6EF4E', flexShrink: 0 }} />
                  )}
                </button>
              ))
            ) : (
              <div className="p-6 text-center">
                <Icon name="Search" size={24} style={{ color: 'rgba(255,255,255,0.2)', margin: '0 auto 8px' }} />
                <p className="text-xs font-oranienbaum text-white/35">No se encontraron empleados</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeSelector;